// F-096 Intégrations : jetons d'API en lecture et webhooks sortants signés.
import { useState } from "react";
import { del, patch, post } from "@core/api";
import { dateTime } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, Input, Loading, Modal, Notice, PageHeader, Select, SimBadge, Table, fieldError } from "@core/ui";
import { Status, useQuick, useRun } from "./shared";

export function IntegrationsPage() {
  const org = useOrg();
  const status = useGet<any>(org.api("/pro/integrations/status"));
  if (!org.can("members")) {
    return (
      <div className="page">
        <PageHeader eyebrow="Professionnels" title="Intégrations (API)" />
        <Notice>La gestion des jetons d’API et des webhooks est réservée aux personnes qui gèrent les membres de l’espace.</Notice>
      </div>
    );
  }
  return (
    <div className="page">
      <PageHeader
        eyebrow="Professionnels"
        title="Intégrations (API)"
        intro="Reliez vos outils métier : jetons d’API en lecture, limités à cet espace, et webhooks signés envoyés lors d’événements choisis."
        actions={status.data ? <SimBadge mode={status.data.webhooks.mode} /> : null}
      />
      {status.data?.webhooks.mode === "simulateur" ? (
        <Notice tone="simulateur" title="Webhooks en mode simulateur">
          Aucun appel réseau n’est effectué : chaque livraison est signée et journalisée comme en conditions réelles, avec une réponse simulée. L’envoi HTTP réel s’active côté serveur.
        </Notice>
      ) : null}
      <ApiTokens />
      <Webhooks />
    </div>
  );
}

function ApiTokens() {
  const org = useOrg();
  const q = useGet<any>(org.api("/pro/api-tokens"));
  const invalidate = useInvalidate();
  const quick = useQuick();
  const { busy, error, run } = useRun();
  const [form, setForm] = useState({ name: "", scopes: [] as string[], expires_in_days: "365" });
  const [created, setCreated] = useState<string | null>(null);
  return (
    <Card title="Jetons d’API">
      <p className="small">
        Appel : <span className="mono">GET /api/v1/pro/ext/properties</span>, <span className="mono">/leases</span> ou <span className="mono">/rent-calls</span> avec l’en-tête <span className="mono">Authorization: Bearer bvk_…</span>. Seule
        l’empreinte du jeton est conservée : il n’est affiché qu’une fois.
      </p>
      {q.isLoading ? <Loading /> : null}
      {q.data?.items.length ? (
        <Table caption="Jetons">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Début du jeton</th>
              <th>Portées</th>
              <th>Dernier usage</th>
              <th>État</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((t: any) => (
              <tr key={t.id}>
                <td>
                  {t.name}
                  <span className="block muted small">Créé le {dateTime(t.created_at)}{t.created_by_name ? ` par ${t.created_by_name}` : ""}</span>
                </td>
                <td className="mono">{t.token_prefix}…</td>
                <td>{(t.scopes as string[]).join(", ")}</td>
                <td>{t.last_used_at ? dateTime(t.last_used_at) : "Jamais"}</td>
                <td>{t.revoked_at ? <Badge>Révoqué</Badge> : t.expires_at && new Date(t.expires_at) < new Date() ? <Badge tone="attention">Expiré</Badge> : <Badge tone="succes">Actif</Badge>}</td>
                <td className="right">
                  {!t.revoked_at ? (
                    <Button variant="danger" small onClick={async () => (await quick(() => post(org.api(`/pro/api-tokens/${t.id}/revoke`)), "Jeton révoqué.")) && invalidate(org.api("/pro/api-tokens"))}>
                      Révoquer
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      {q.data && !q.data.items.length ? <Empty title="Aucun jeton">Créez un jeton pour chaque outil qui doit lire vos données.</Empty> : null}
      {created ? (
        <Notice tone="succes" title="Copiez ce jeton maintenant : il ne sera plus affiché">
          <p className="pro-secret">{created}</p>
        </Notice>
      ) : null}
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          const r: any = await run(() => post(org.api("/pro/api-tokens"), { name: form.name, scopes: form.scopes, expires_in_days: form.expires_in_days ? parseInt(form.expires_in_days, 10) : null }), "Jeton créé.");
          if (r) {
            setCreated(r.token);
            setForm({ name: "", scopes: [], expires_in_days: "365" });
            invalidate(org.api("/pro/api-tokens"));
          }
        }}
      >
        <h3>Nouveau jeton</h3>
        <div className="form-row">
          <Input label="Nom de l’outil" required value={form.name} error={fieldError(error, "name")} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Select
            label="Validité"
            value={form.expires_in_days}
            error={fieldError(error, "expires_in_days")}
            options={[
              { value: "90", label: "90 jours" },
              { value: "365", label: "1 an" },
              { value: "730", label: "2 ans" },
              { value: "", label: "Sans expiration" },
            ]}
            onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })}
          />
        </div>
        <fieldset className="pro-checklist">
          <legend>Portées (lecture seule)</legend>
          {(q.data?.scopes ?? []).map((s: any) => (
            <Checkbox key={s.value} label={s.label} checked={form.scopes.includes(s.value)} onChange={(v) => setForm({ ...form, scopes: v ? [...form.scopes, s.value] : form.scopes.filter((x) => x !== s.value) })} />
          ))}
        </fieldset>
        {fieldError(error, "scopes") ? <p className="bv-champ-aide" role="alert">{fieldError(error, "scopes")}</p> : null}
        <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
        <div>
          <Button type="submit" variant="secondaire" busy={busy}>
            Créer le jeton
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Webhooks() {
  const org = useOrg();
  const q = useGet<any>(org.api("/pro/webhooks"));
  const invalidate = useInvalidate();
  const quick = useQuick();
  const { busy, error, run } = useRun();
  const [form, setForm] = useState({ url: "", description: "", events: [] as string[] });
  const [secret, setSecret] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Card title="Webhooks sortants">
      <p className="small">
        Chaque livraison est un <span className="mono">POST</span> JSON signé : en-tête <span className="mono">X-Bailivo-Signature: t=&lt;horodatage&gt;,v1=&lt;HMAC-SHA256(secret, horodatage + "." + corps)&gt;</span>. Vérifiez la signature et
        l’horodatage (5 minutes) avant de traiter l’événement.
      </p>
      {q.isLoading ? <Loading /> : null}
      {q.data?.items.length ? (
        <Table caption="Webhooks">
          <thead>
            <tr>
              <th>Adresse</th>
              <th>Événements</th>
              <th>Dernière livraison</th>
              <th>État</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((w: any) => (
              <tr key={w.id}>
                <td>
                  <span className="mono small">{w.url}</span>
                  {w.description ? <span className="block muted small">{w.description}</span> : null}
                  <span className="block muted small">Secret se terminant par {w.secret_hint}</span>
                </td>
                <td>{(w.events as string[]).join(", ")}</td>
                <td>{w.last_status ? <Status kind="delivery" value={w.last_status} /> : "—"}</td>
                <td>{w.active ? <Badge tone="succes">Actif</Badge> : <Badge>En pause</Badge>}</td>
                <td className="right">
                  <div className="row">
                    <Button variant="secondaire" small onClick={async () => (await quick(() => post(org.api(`/pro/webhooks/${w.id}/test`)), "Événement de test envoyé.")) && invalidate(org.api("/pro/webhooks"))}>
                      Tester
                    </Button>
                    <Button variant="discret" small onClick={() => setOpen(w.id)}>
                      Livraisons
                    </Button>
                    <Button variant="discret" small onClick={async () => (await quick(() => patch(org.api(`/pro/webhooks/${w.id}`), { active: !w.active }), w.active ? "Webhook mis en pause." : "Webhook réactivé.")) && invalidate(org.api("/pro/webhooks"))}>
                      {w.active ? "Mettre en pause" : "Réactiver"}
                    </Button>
                    <Button variant="danger" small onClick={async () => (await quick(() => del(org.api(`/pro/webhooks/${w.id}`)), "Webhook supprimé.")) && invalidate(org.api("/pro/webhooks"))}>
                      Supprimer
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      {q.data && !q.data.items.length ? <Empty title="Aucun webhook">Ajoutez l’adresse HTTPS de votre outil pour être prévenu des événements.</Empty> : null}
      {secret ? (
        <Notice tone="succes" title="Secret de signature : copiez-le maintenant, il ne sera plus affiché">
          <p className="pro-secret">{secret}</p>
        </Notice>
      ) : null}
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          const r: any = await run(() => post(org.api("/pro/webhooks"), form), "Webhook créé.");
          if (r) {
            setSecret(r.secret);
            setForm({ url: "", description: "", events: [] });
            invalidate(org.api("/pro/webhooks"));
          }
        }}
      >
        <h3>Nouveau webhook</h3>
        <div className="form-row">
          <Input label="Adresse HTTPS" required value={form.url} error={fieldError(error, "url")} placeholder="https://outil.exemple.fr/bailivo" onChange={(e) => setForm({ ...form, url: e.target.value })} />
          <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <fieldset className="pro-checklist">
          <legend>Événements</legend>
          {(q.data?.events ?? []).map((ev: any) => (
            <Checkbox key={ev.value} label={ev.label} help={ev.value} checked={form.events.includes(ev.value)} onChange={(v) => setForm({ ...form, events: v ? [...form.events, ev.value] : form.events.filter((x) => x !== ev.value) })} />
          ))}
        </fieldset>
        {fieldError(error, "events") ? <p className="bv-champ-aide" role="alert">{fieldError(error, "events")}</p> : null}
        <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
        <div>
          <Button type="submit" variant="secondaire" busy={busy}>
            Ajouter le webhook
          </Button>
        </div>
      </form>
      {open ? <Deliveries id={open} onClose={() => setOpen(null)} /> : null}
    </Card>
  );
}

function Deliveries({ id, onClose }: { id: string; onClose: () => void }) {
  const org = useOrg();
  const q = useGet<any>(org.api(`/pro/webhooks/${id}/deliveries`));
  const invalidate = useInvalidate();
  const quick = useQuick();
  return (
    <Modal open onClose={onClose} title="Journal de livraison">
      {q.isLoading ? <Loading /> : null}
      {q.data && !q.data.items.length ? <p className="muted">Aucune livraison.</p> : null}
      <ul className="list-plain stack">
        {(q.data?.items ?? []).map((d: any) => (
          <li key={d.id} className="stack-sm">
            <div className="row-between">
              <span>
                <strong>{d.event}</strong> · {dateTime(d.created_at)} · tentative {d.attempt} <SimBadge mode={d.mode} />
              </span>
              <Status kind="delivery" value={d.status} />
            </div>
            <span className="small">
              {d.http_status ? `Réponse HTTP ${d.http_status}` : "Pas de réponse"}
              {d.error ? ` — ${d.error}` : ""}
            </span>
            <details>
              <summary className="small">Corps et signature</summary>
              <pre className="pro-secret small">{d.body}</pre>
              <p className="pro-secret small">{d.signature}</p>
            </details>
            {d.status === "echec" ? (
              <div>
                <Button variant="secondaire" small onClick={async () => (await quick(() => post(org.api(`/pro/webhook-deliveries/${d.id}/retry`)), "Nouvelle tentative envoyée.")) && invalidate(org.api("/pro/webhooks"))}>
                  Renvoyer
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </Modal>
  );
}
